import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../shared/services/firebase';
import { USE_FIREBASE, getBackendService } from '../../shared/services/apiConfig';
import firebaseApp from '../../shared/services/firebase';
import CustomerAvatar from '../../shared/design/components/CustomerAvatar';
import { Button } from '../../shared/design';
import { CheckIcon, XIcon, GiftIcon } from './icons/UiIcons';

interface PendingRedemptionsProps {
  onSelectCustomer?: (id: string | number) => void;
}

interface ConfirmResult {
  success: boolean;
  rewardId: string;
  description: string;
  userName: string;
  cost: number;
  debitedField: 'points' | 'stamps' | 'none';
  newBalance: number;
}

interface Redemption {
  id: string;
  rewardId: string;
  userId: string;
  userName: string;
  description: string;
  restaurantId: string;
  status: 'pending' | 'confirmed' | 'rejected' | 'cancelled';
  requestedAt?: any;
  expiresAtMs?: number;
}

function formatRelative(ms?: number) {
  if (!ms) return '';
  const remaining = Math.max(0, Math.round((ms - Date.now()) / 1000));
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default function PendingRedemptions({ onSelectCustomer }: PendingRedemptionsProps = {}) {
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [actionFor, setActionFor] = useState<string | null>(null);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      // Mode démo (sans Firebase Auth) : on prend l'établissement de la session locale.
      (async () => {
        const backend = await getBackendService();
        const rid = backend.getCurrentUser()?.restaurantId;
        if (rid) setRestaurantId(String(rid));
        setLoading(false);
      })();
      return;
    }
    (async () => {
      const snap = await getDoc(doc(db, 'users', uid));
      const rid = snap.exists() ? (snap.data() as any).restaurantId : null;
      setRestaurantId(rid);
    })();
  }, []);

  useEffect(() => {
    if (!restaurantId || !USE_FIREBASE) return;
    const q = query(
      collection(db, 'rewardRedemptions'),
      where('restaurantId', '==', restaurantId),
      where('status', '==', 'pending'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: Redemption[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setRedemptions(list);
      setLoading(false);
    });
    return unsub;
  }, [restaurantId]);

  const activeRedemptions = useMemo(
    () => redemptions.filter((r) => !r.expiresAtMs || r.expiresAtMs > now),
    [redemptions, now],
  );

  const confirm = async (r: Redemption) => {
    setActionFor(r.id);
    try {
      // Délègue à la CF qui débite le solde du membership atomiquement.
      const fn = httpsCallable<{ redemptionId: string }, ConfirmResult>(
        getFunctions(firebaseApp),
        'confirmRewardRedemption',
      );
      const { data } = await fn({ redemptionId: r.id });
      if (!data.success) {
        alert('Validation refusée par le serveur.');
        return;
      }
      // Le snapshot Firestore retire la ligne automatiquement (status passe à confirmed).
      // On affiche un récap si quelque chose a été débité.
      if (data.cost > 0 && data.debitedField !== 'none') {
        const unit = data.debitedField === 'stamps' ? 'tampons' : 'pts';
        console.info(
          `[redemption] -${data.cost} ${unit} pour ${data.userName} (solde restant ${data.newBalance})`,
        );
      }
    } catch (e: any) {
      console.error('confirm redemption error', e);
      const code = e?.code || '';
      const msg = e?.message || '';
      if (code.includes('failed-precondition') && msg.includes('insuffisant')) {
        alert(`Solde insuffisant : ${msg}`);
      } else if (code.includes('deadline-exceeded')) {
        alert('Cette demande a expiré. Le client doit refaire une demande.');
      } else if (code.includes('not-found')) {
        alert('Demande introuvable.');
      } else {
        alert('Impossible de valider la récompense.');
      }
    } finally {
      setActionFor(null);
    }
  };

  const reject = async (r: Redemption) => {
    if (!window.confirm(`Refuser la récompense "${r.description}" pour ${r.userName} ?`)) return;
    setActionFor(r.id);
    try {
      await updateDoc(doc(db, 'rewardRedemptions', r.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('reject redemption error', e);
    } finally {
      setActionFor(null);
    }
  };

  if (!restaurantId) {
    return (
      <div className="p-8 text-center text-gray-500">
        Vous devez être connecté comme restaurateur pour voir cette page.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title">Récompenses à valider</h1>
          <p className="page-subtitle">
            Les clients vous demandent de valider leurs récompenses. Confirmez pour créditer.
          </p>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-xs font-bold ${
            activeRedemptions.length > 0
              ? 'bg-orange-600 text-white animate-pulse'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
          }`}
        >
          {activeRedemptions.length} en attente
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400 dark:text-gray-500">Chargement…</div>
      ) : activeRedemptions.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl p-12 text-center border border-gray-100 dark:border-gray-800">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-primary/10 text-brand-primary mb-4">
            <GiftIcon className="w-7 h-7" />
          </div>
          <p className="font-semibold text-gray-800 dark:text-gray-200">Aucune demande en attente</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Les nouvelles demandes apparaîtront ici en temps réel.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeRedemptions.map((r) => {
            const expired = r.expiresAtMs && r.expiresAtMs <= now;
            const isActing = actionFor === r.id;
            return (
              <div
                key={r.id}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-orange-200 dark:border-orange-500/30 shadow-sm dark:shadow-black/20 p-5"
              >
                <div className="flex items-start gap-3 mb-3">
                  <CustomerAvatar
                    name={r.userName}
                    size="md"
                    verified
                    onClick={onSelectCustomer ? () => onSelectCustomer(r.userId) : undefined}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 text-xs font-black px-2 py-1 rounded-full">
                        EN ATTENTE
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {formatRelative(r.expiresAtMs)} restantes
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-2">{r.description}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Demandé par{' '}
                      {onSelectCustomer ? (
                        <button
                          type="button"
                          onClick={() => onSelectCustomer(r.userId)}
                          className="font-semibold text-brand-primary hover:underline"
                        >
                          {r.userName}
                        </button>
                      ) : (
                        <span className="font-semibold">{r.userName}</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => reject(r)}
                    disabled={isActing || expired}
                    leftIcon={<XIcon className="w-4 h-4" />}
                    className="flex-1"
                  >
                    Refuser
                  </Button>
                  <Button
                    variant="success"
                    size="md"
                    onClick={() => confirm(r)}
                    disabled={isActing || expired}
                    loading={isActing}
                    leftIcon={!isActing ? <CheckIcon className="w-4 h-4" /> : undefined}
                    className="flex-[2]"
                  >
                    {isActing ? 'Validation…' : 'Valider la récompense'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
